<template>
  <div :id="id+'Container'" ref="container" class="vcl-table" :style="tableStyles">
    <VpTableBulkAction
      ref="bulkActions"
      :selected="selectedRows" :actions="bulkActionsComputed"
      :styles="bulkActionStyles"
      :disabled="!bulkActionsComputed.length"
      @visible="mixinSetHeight && mixinSetHeight()"
    />
    <vp-table-tools ref="tools" v-if="showFilters || showSearch" v-bind="toolProps" v-model="activeFilters" @search="updateInternalSearch">
      <template #buttons>
        <slot name="tools-controller" />
      </template>
      <template #content>
        <slot name="tools-content" />
      </template>
    </vp-table-tools>
    <slot name="summary"></slot>
    <table :id="id+'Table'" v-bind="{'data-navigation': enableCellNavigation }">
      <thead class="vcl-table__header" v-bind="{ 'data-sticky': stickyHeader}">
        <tr class="vcl-table__row">
          <th v-if="allowRowSelection !== 0" class="vcl-table__selection" :class="[{ 'show-row-on-breakpoint': collapseOptions.enabled }, `${collapseOptions.breakpoint}-breakpoint`]" title="Select all Rows">
            <VpTableCheckbox ref='tableCheckbox' v-bind="selectAllProps" :class="[{'hide-on-breakpoint': collapseOptions.enabled }, `${collapseOptions.breakpoint}-breakpoint`]" :styles="{ tabindex: getTabindex('checkbox')}" @selected="selectAllRowsClicked($event, true)" />
          </th>
          <template v-for="(column, index) in columns">
            <th
              :key="column.id"
              :style="columnStyle(column.id)"
              class="sortable"
              :class="[{ 'hide-on-breakpoint': collapseOptions.enabled && !isCellVisible(column, index), 'expand-on-breakpoint': collapseOptions.enabled && isCellVisible(column, index) }, `${collapseOptions.breakpoint}-breakpoint`]"
              :title="columnTooltip(column.id)"
              :aria-sort="addAriaSort(column.id)"
              aria-describedby="sort-message"
              tabindex="0"
              role="tableHeader"
              @mousedown="handleHeaderClick($event, column)"
              @keydown.enter="handleHeaderClick($event, column)"
            >
              <span id="sort-message">This Table is Sortable</span>
              <div class="column-header" :class="[column.align ? 'align-'+column.align : '']">
                <div class="column-header__label">
                  <slot name="header-cell" :column="column" :sort="{ id: internalSortColumn, direction: internalSortDirection }">
                    <span class="column-header__label__primary">{{ column.label }}</span>
                    <i v-if="column.subLabel" class="column-header__label__sub">{{ column.subLabel }}</i>
                  </slot>
                </div>
                <div v-if="!column.hideSort || column.hideSort !== 1" class="column-header__sort-icon" role="button" aria-label="Sort Column">
                  <vp-icon :class="getSortIcon(column)" :icon="getSortIcon(column)" />
                </div>
                <slot name="header-cell-suffix" :column="column" :sort="{ id: internalSortColumn, direction: internalSortDirection }"/>
              </div>
            </th>
          </template>
          <th v-if="actions && actions.length" class="vcl-table__action">Action</th>
        </tr>
      </thead>
      <tbody class="vcl-table__body">
        <template v-for="(row,index) in rowState">
          <tr
            v-if="rowShown(index)"
            :key="index"
            :data-row="index"
            :class="[
              'vcl-table__row',
              fireEventOnRowClick ? 'clickable': '',
              { 'rowSelected': isRowSelected(index) === true,  },
              { 'is-row-open': isOpen(index) },
              `${collapseOptions.breakpoint}-breakpoint`,
              [borderAlerts.enabled && !hasExpandedContent(row.refId, true) ? borderAlertStyle(rowData[row.refId]) : {}]
            ]"
            :style="row.styles"
            @click="$emit('row-click', rowData[row.refId], row.refId)"
          >
            <td
              v-if="allowRowSelection !== 0"
              :ref="`row_${index}`"
              :tabindex="getTabindex('td')"
              :class="[
              'vcl-table__selection',
                { 'vcl-table__selection--custom': row.customCheckbox,
                'show-row-on-breakpoint': collapseOptions.enabled },
                `${collapseOptions.breakpoint}-breakpoint`
              ]"
                @keydown="handleCellNavigation({data: row, index}, {data: { id: 'selection'}, index: 0}, $event)"
              >
              <slot name="checkbox" v-bind="{row, index}"/>
              <VpTableCheckbox :id="'Checkbox'+index" :index="index" :selected="row['selected']" :class="[{ 'hide-on-breakpoint': collapseOptions.enabled }, `${collapseOptions.breakpoint}-breakpoint`]" :styles="{ tabindex: getTabindex('checkbox') }" @selected="selectItem($event,row.refId)" />
              <vp-icon icon="down-arrow" class="toggle-row-icon show-on-breakpoint" :class="[{ 'is-icon-open': isOpen(index), 'hide-collapse-trigger': !collapseOptions.enabled }, `${collapseOptions.breakpoint}-breakpoint`]" @click.stop="collapseOptions.enabled && toggleRow(index, isOpen(index) === true ? false : true)" />
            </td>
            <template v-for="(column, i) in columns" >
              <td
                :ref="`row_${index}`"
                :tabindex="getTabindex('td')"
                @keydown="handleCellNavigation({data: row, index}, {data: column, index: i + 1}, $event)"
                role="cell"
                :key="'table-cell-'+i"
                :class="{
                  [`${column.align}Align`]: column.align,
                  'hide-on-breakpoint': collapseOptions.enabled && !isCellVisible(column, i),
                  [`${collapseOptions.breakpoint}-breakpoint`]: collapseOptions.breakpoint,
                  'emptyCell': row['display'][column.id] === '',
                }"
                :style="columnStyle(column.id)"
              >
                <slot name="cell" :column="column" :display="row['display'][column.id]" :row="rowData[row.refId]" :rowRef="row.refId">
                  <span v-html="row['display'][column.id]"></span>
                </slot>
              </td>
            </template>
            <td
              v-if="actions && actions.length"
              class="vcl-table__action"
              :ref="`row_${index}`" :tabindex="getTabindex('td')"
              @keydown="handleCellNavigation({data: row, index}, {data: { id: 'actions' }, index: columns.length + 1}, $event)"
            >
              <VpTableAction v-if="rowData[row.refId]['actions'] !== undefined" :item="rowData[row.refId]" :options="rowData[row.refId]['actions']" />
              <VpTableAction v-else :item="rowData[row.refId]" :options="actions" />
            </td>
          </tr>
          <tr v-if="collapseOptions.enabled && rowShown(index) && row['expanded']" :key="'collapse-row'+index" :data-row="index" class="collapsed-row show-on-breakpoint" :class="`${collapseOptions.breakpoint}-breakpoint`">
            <td :colspan="(collapseOptions.visible.length + 2)" style="margin: 0; padding: 0">
              <template v-for="(column, i) in columns">
                <slot name="collapsed-cell" :column="column" :index="i" :display="row['display'][column.id]" :row="rowData[row['refId']]">
                  <p v-if="!isCellVisible(column, i)" :key="'collapse-table-cell-'+column.id+i" class="collapsed-row__content"><strong>{{ column.label }}:</strong> {{ row['display'][column.id] }}</p>
                </slot>
              </template>
            </td>
          </tr>
          <tr v-if="hasExpandedContent(row.refId)" class="vcl-table__row expanded-row" ref="expanded-row">
            <slot name="expandable-row-override" v-bind="{ expandedRowSpan, rowRef: row.refId, row: rowData[row['refId']], classes: { [`${columns[0].align}Align`]: columns[0].align } }">
              <td :colspan="expandedRowSpan" :class="{ [`${columns[0].align}Align`]: columns[0].align }">
                <slot name="expandable-row" v-bind="{ expandedRowSpan, rowRef: row.refId, row: rowData[row['refId']], classes: { [`${columns[0].align}Align`]: columns[0].align } }" />
              </td>
            </slot>
          </tr>
        </template>
      </tbody>
    </table>
    <div v-if="isEmptyTable" class="vcl-table__empty-state">
      <slot name="empty-state">
        <div class="vcl-table__empty-state-content">
          <h4 v-if="emptyStateMessage && emptyStateMessage.title" class="vcl-table__empty-state-title">{{ emptyStateMessage.title }}</h4>
          <p v-if="emptyStateMessage && emptyStateMessage.description" class="vcl-table__empty-state-description" v-html="emptyStateMessage.description"></p>
        </div>
      </slot>
    </div>
    <div class="vcl-table__pagination-controls">
      <div v-if="showCsvExport" class="vcl-table__csv_export">
        <vp-button class="no-fill medium"  :disabled="isEmptyTable" :styles="{ 'border': '1px solid #e4e4e4', 'fontWeight': 500, 'hoverBorder': '1px solid #777', 'textTransform': 'none' }" @click="exportCsv('all')">{{ $t('exportCsvFile') }}</vp-button>
      </div>
      <div v-if="showFooter === 1" class="vcl-table__footer">
        <div class="vcl-table__results">
          <span v-if="totalRows > 1">{{ totalRows }} {{ $t('results') }}</span>
          <span v-if="totalRows === 1">{{ totalRows }} {{ $t('result') }}</span>
          <span v-if="totalRows === 0">{{ $t('noResults') }}</span>
        </div>
        <VpTablePagination :id="id+'Pagination'" :initial-page="internalPageNumber" :initial-page-size="internalItemsPerPage" :page-size-list="itemsPerPageOptions" :allow-page-size-updates="allowPageSizeUpdates" :last-page="pageCount" @updated="pageUpdated" />
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { ExportToCsv } from 'export-to-csv';
/** Components */
import VpTableCheckbox from './VpTableCheckbox.vue';
import VpTablePagination from './VpTablePagination.vue';
import VpTableAction from './VpTableAction.vue';
import VpTableBulkAction from './VpTableBulkAction.vue';
import VpTableTools from './VpTableTools.vue';
import VpIcon from '../vp-icon/VpIcon.vue';

/** Mixins */
import stickyTable from './mixins/stickyHeader';
import navigation from './mixins/navigation';
import queryStorage from './mixins/queryStorage';
import refresh from './mixins/refresh';

// this is defined here because we need it to be non-reactive
let shownRowIndex = 0;

export default {
  name: 'VpTable',
  mixins: [stickyTable, navigation, queryStorage, refresh],
  components: {
    VpTableCheckbox,
    VpTablePagination,
    VpTableAction,
    VpTableBulkAction,
    VpTableTools,
    VpIcon,
  },
  props: {
    busy: {
      type: Boolean,
      default: false,
    },
    id: {
      type: String,
      default: '',
    },
    filters: {
      type: Array,
      default: () => [],
    },
    columns: {
      type: Array,
      default: () => [],
    },
    rowData: {
      type: Array,
      default: () => [],
    },
    actions: {
      type: Array,
      default: () => [],
    },
    bulkActions: {
      type: Array,
      default: () => [],
    },
    showSearch: {
      type: [Number, Boolean],
      default: 1,
    },
    showFooter: {
      type: Number,
      default: 1,
    },
    showCsvExport: {
      type: Number,
      default: 0,
    },
    allowPartialCsvExport: {
      type: Number,
      default: 0,
    },
    customCsvExport: {
      type: Number,
      default: 0,
    },
    showFilters: {
      type: [Number, Boolean],
      default: 1,
    },
    width: {
      type: String,
      default: '',
    },
    dataMode: {
      type: String,
      default: 'client',
      validator: (value) => {
        const types = ['server', 'client'];
        const isValid = types.includes(value);
        if (!isValid) {
          console.error(`Invalid prop: dataMode must be one of these values ${types}, received: `, [value, typeof value]);
        }
        return isValid;
      }
    },
    page: {
      type: Number,
      default: 0,
    },
    sortColumn: {
      type: String,
      default: '',
    },
    sortDirection: {
      type: String,
      default: '',
    },
    search: {
      type: String,
      default: '',
    },
    searchDebounce: {
      type: Number,
      default: 0,
    },
    allowRowSelection: {
      type: Number,
      default: 0,
    },
    allowPageSizeUpdates: {
      type: Number,
      default: 1,
    },
    enableCellNavigation: {
      type: Number,
      default: null,
      validator: (value) => [0, 1].includes(value),
    },
    selectAllRows: {
      type: [Number, Boolean],
      default: false,
      validator: (value) => {
        const valid = typeof value === 'number' ? [0, 1].includes(value) : typeof value === 'boolean';
        if (!valid) {
          console.error('Invalid prop: selectAllRows must be either a Number || Boolean, received: ', [value, typeof value]);
        }
        return valid;
      },
    },
    selectAllOverride: {
      type: Boolean,
      default: false,
    },
    itemsPerPage: {
      type: Number,
      default: 25,
    },
    fireEventOnRowClick: {
      type: Number,
      default: 1,
    },
    emptyStateMessage: {
      type: Object,
      default: () => {},
    },
    apiItemCount: {
      type: Number,
      default: 0,
    },
    options: {
      type: Object,
      default() {
        return {};
      },
    },
    styles: {
      type: Object,
      default: () => ({}),
    },
    borderAlerts: {
      type: Object,
      default: () => {
        return {
          // Enabling borderAlerts allows a colored left-border on a row based on given properties
          enabled: false,
          properties: [
            // Will assign designated color if row has a key/value pair that matches
            // Array should be ordered from highest priority to lowest
            // No border will be added to a row that doesn't satisfy any of the requirements
            {
              key: 'some-key',
              value: 'some-value',
              color: 'some-color'
            }
          ]
        }
      },
    },
    expandedRow: {
      type: [Number, Array],
      default: null,
    },
    updateHandler: {
      type: Function,
      default: null,
    },
  },
  emits: ['page-model-updated', 'row-click', 'selection-updated', 'add-button-clicked', 'table-props-updated', 'csv-export', 'filters-updated'],
  data() {
    return {
      applyFilter: 0,
      activeFilters: [],
      internalAllSelected: Boolean(this.selectAllRows),
      internalPageNumber: this.page,
      internalSortColumn: this.sortColumn,
      originalSortColumnProp: this.sortColumn,
      internalSortDirection: this.sortDirection,
      originalSortDirectionProp: this.sortDirection,
      internalItemsPerPage: this.itemsPerPage,
      itemsPerPageOptions: [5, 15, 25, 50, 100, 150],
      pageCount: 0,
      rowState: [],
      searchLastEventFired: 0,
      searchEventQueued: 0,
      searchText: this.search,
      totalRows: 0,
      openedRow: null,
      collapse: {
        enabled: true,
        visible: [0],
        breakpoint: '40em',
      },
    };
  },
  computed: {
    bulkActionsComputed() {
      if (this.showCsvExport && this.allowPartialCsvExport) {
        return this.bulkActions.concat({ label: 'Export to CSV', value: 'csv-export', onClick: this.exportSelectedRows });
      }
      return this.bulkActions;
    },
    bulkActionStyles() {
      const bActions = (this.styles || {}).bulkActions || {};
      const styles  = {
        top: bActions.top || '55px',
        height: bActions.height || '55px',
      };
      return (!this.stickyHeader) ? styles : { ...styles, ...this.mixinBulkActionStyles };
    },
    expandedRowSpan() {
      let count = this.columns.length;
      if (this.allowRowSelection) count += 1;
      if (this.actions) count += 1;
      /** TODO: Deduct hidden columns from count */
      return count;
    },
    tableStyles() {
      const enableSummaryBorder = !this.styles.border && Boolean(this.$slots.summary);
      const styles = {
        '--table-border-top': this.styles.border || (this.stickyHeader && !enableSummaryBorder ? 'none' : '1px solid #dbdbdb'),
      };
      return !this.stickyHeader ? styles : { ...styles, ...this.mixinTableStyles };
    },
    internalColumnData: function internalColumnData() {
      const cols = {};
      this.columns.forEach((column) => {
        cols[column.id] = column;
      });
      return cols;
    },
    selectedRows: function selectedRows() {
      let rows = [];
      for (var i = 0; i < this.rowState.length; i++) {
        if (this.rowState[i].selected) {
          rows.push(this.rowData[this.rowState[i].refId]);
        }
      }
      return rows;
    },
    selectAllProps() {
      const props = {
        selected: Boolean(this.selectedRows.length || this.internalAllSelected),
        icon: 'minus',
      };
      if (this.internalAllSelected) props.icon = 'white-check';
      return props;
    },
    collapseOptions() {
      const options = {
        enabled: false,
        visible: [0],
        breakpoint: 'small',
      };

      return this.getOptions(options, this.options.collapse);
    },
    isEmptyTable() {
      const isEmptyData = !this.rowData || !this.rowData.length;
      const isEmptyFilter = this.applyFilter === 1 ? this.rowState.every((row) => !row.shown) : false;
      return isEmptyData || isEmptyFilter;
    },
    csvExportHeaders() {
      const headers = [];
      this.columns.forEach((column) => {
        if (!column.excludeFromExport) {
          headers.push(column.label);
        }
      });
      return headers;
    },
    csvExportOptions() {
      const options = {
        fieldSeparator: ',',
        quoteStrings: '"',
        decimalSeparator: '.',
        showLabels: true,
        showTitle: false,
        useTextFile: false,
        useBom: true,
        useKeysAsHeaders: false,
        headers: this.csvExportHeaders,
      };
      return options;
    },
    csvExportHasHiddenColumns() {
      let hasHiddenColumns = false;
      this.columns.forEach((column) => {
        if (column.excludeFromExport) {
          hasHiddenColumns = true;
        }
      });
      return hasHiddenColumns;
    },
    toolProps() {
      const tools = this.styles?.tools || {};
      const options = {
        search: this.searchText,
        styles: {
          top: this.mixinTableStyles['--table-tools-top'],
          index: this.mixinTableStyles['--table-tools-index'],
          ...tools
        }
      };
      const sharedProps = ['busy', 'filters', 'searchDebounce', 'showFilters', 'showSearch', 'stickyHeader'];
      sharedProps.forEach((key) => {
        options[key] = this[key];
      });
      return options;
    }
  },
  watch: {
    apiItemCount: {
      immediate: true,
      handler(value) {
        if (this.dataMode === 'server') {
          this.totalRows = value;
          this.pageCount = Math.ceil(value / this.internalItemsPerPage);
        }
      }
    },
    activeFilters(current, previous) {
      this.$emit('filters-updated', current);
      if (this.queryReady) {
        this.internalPageNumber = 0;
        this.tablePropsUpdated();
      }
    },
    rowData: {
      immediate: true,
      handler(current = [], previous = []) {
        const hasChanged = JSON.stringify(current) !== JSON.stringify(previous);
        if (this.dataMode === 'client') {
          this.totalRows = this.rowData.length;
          this.pageCount = Math.ceil(this.totalRows / this.internalItemsPerPage);
          return this.updateData(current);
        }
        /** If table is not on first page, and returns with empty payload, refetch data */
        const hasMoreData = Boolean(this.apiItemCount > 0 && this.internalPageNumber > 0 && (!current || !current.length));
        if (this.enableQueries && hasMoreData) return this.resetTable();
        return (hasChanged) ? this.updateData(current) : null;
      },
    },
    selectedRows: {
      deep: true,
      handler() {
        this.internalAllSelected = this.checkIfAllRowsSelected();
      }
    },
  },
  mounted() {
    if (this.$refs.container) {
      this.$refs.container.style.width = `${this.width}`;
    }
  },
  methods: {
    hasExpandedContent(refId, skipValidation = false) {
      const validSlots = Boolean(this.$scopedSlots['expandable-row']) || Boolean(this.$scopedSlots['expandable-row-override']);
      if (!skipValidation && (!this.columns.length || !validSlots)) return false;
      if (Array.isArray(this.expandedRow)) return this.expandedRow.includes(refId)
      return this.expandedRow === refId;
    },
    updateInternalSearch(value) {
      this.searchText = value ? String(value).trim() : '';
      return this.tableSearchUpdated(true);
    },
    resetTable() {
      const pageCount = Math.ceil(this.apiItemCount / this.internalItemsPerPage);
      this.internalPageNumber = (this.apiItemCount && pageCount) ? pageCount - 1 : 0;
      this.internalItemsPerPage = (this.apiItemCount && this.itemsPerPage) || 15;
      return this.tablePropsUpdated();
    },
    updateData(payload) {
      let updatedRowData = [];
      if (payload !== undefined) {
        updatedRowData = payload;
      } else {
        updatedRowData = [];
        this.totalRows = 0;
      }

      Vue.set(this, 'rowState', []);

      for (let i = 0; i < updatedRowData.length; i += 1) {
        // everything defaults to being unselected, unexpanded, shown, and a filter match
        const state = {
          selected: false,
          expanded: false,
          filterMatch: true,
          refId: i,
          styles: updatedRowData[i].styles,
          customCheckbox: Boolean(updatedRowData[i].customCheckbox),
        };

        // if data is all client side, we must determine row visibility
        // if data is API driven, we only receive the shown rows
        if (this.dataMode === 'client') {
          state.shown = false;
        } else {
          state.shown = true;
        }

        state.display = this.columns.reduce((d, column) => {
          const { id, formatter = '' } = column;
          const display = d;
          display[id] = (formatter !== undefined) && (formatter !== '')
            ? this.applyFormatter(formatter, updatedRowData[i][id])
            : updatedRowData[i][id];
          return display;
        }, {});

        /**
         * if group properties exist, retain values at root of parsed object
         * Used for syncing select state across grouped rows
        */
        if (updatedRowData[i].$group) {
          state.group = updatedRowData[i].$group;
        }

        /**
         * if selectAllRows is enabled, default selected state to true
         * Used in Changelog Modal, and other components within the suite-engagement
        */
        if (this.selectAllRows) {
          state.selected = state.filterMatch;
        }

        Vue.set(this.rowState, i, state);
      }

      if (this.internalSortColumn != '') {
        this.rowState.sort(this.rowSort());
      }

      // if we are client-side, call update-row-visiblity to apply pagination
      if (this.dataMode === 'client') {
        return (this.searchText) ? this.tableSearchUpdated(false) : this.updateRowVisibility(true);
      }
    },
    pageUpdated(pagination) {
      const { page = null, pageSize = null } = pagination;
      if (page || page === 0) {
        this.internalPageNumber = page;
      } else if (pageSize) {
        this.internalPageNumber = 0;
        this.internalItemsPerPage = pageSize;
        this.pageCount = Math.ceil(this.totalRows / this.internalItemsPerPage);
      }

      if (this.dataMode === 'client') {
        this.updateRowVisibility();
      }
      this.tablePropsUpdated();
    },
    tablePropsUpdated() {
      // inform parent component that the page/sort model has updated
      const evt = {
        page: this.internalPageNumber,
        pageSize: this.internalItemsPerPage,
        searchText: this.searchText,
        sortColumn: this.internalSortColumn,
        sortDirection: this.internalSortDirection,
        totalRows: this.totalRows,
        filters: this.activeFilters,
      };
      this.$emit('table-props-updated', evt);
      this.querySave && this.querySave();
      /** call parent handler to fetch data, if provided */
      return this.dataMode === 'client' ? null : Promise.resolve(this.updateHandler && this.updateHandler(evt))
        .catch(err => console.error(err.message));
    },
    async updateRowVisibility(enableSearchCheck = false) {
      // update-row-visbility
      // + Apply pagination - determine which rows are visible based on
      //   current page and items per page.
      // + If filter is applied,
      if (enableSearchCheck) {
        await Promise.resolve(this.querySanitizeCurrentPage && this.querySanitizeCurrentPage({ toggleSave: true }));
      }
      const pageNum = this.internalPageNumber;
      const pageSize = this.internalItemsPerPage;
      const rowCount = this.rowData.length;
      const startIndex = pageNum * pageSize;

      shownRowIndex = 0;

      let endIndex = ((pageNum + 1) * pageSize) - 1;
      if (this.applyFilter) {
        // set everything to shown=false first
        for (let i = 0; i < rowCount; i += 1) {
          Vue.set(this.rowState[i], 'shown', false);
        }

        // if we only have one page worth of shown rows, just iterate over the collection and
        // set everything with a filter match to shown=true
        if (rowCount <= pageSize) {
          for (let i = 0; i < this.rowData.length; i += 1) {
            if (this.rowState[i].filterMatch) {
              Vue.set(this.rowState[i], 'shown', true);
            }
          }
        } else {
          // pull from the correct page worth of filtered data
          let filterMatchNum = 0;
          for (let i = 0; i < rowCount; i += 1) {
            if (this.rowState[i].filterMatch) {
              if ((filterMatchNum >= startIndex) && (filterMatchNum <= endIndex)) {
                Vue.set(this.rowState[i], 'shown', true);
              }
              filterMatchNum += 1;
            }
          }
        }
      } else if (this.totalRows > pageSize) {
        // do we need to paginate?
        if (endIndex >= rowCount) {
          endIndex = rowCount - 1;
        }
        for (let i = 0; i < rowCount; i += 1) {
          if ((i >= startIndex) && (i <= endIndex)) {
            Vue.set(this.rowState[i], 'shown', true);
          } else {
            Vue.set(this.rowState[i], 'shown', false);
          }
        }
      } else {
        for (let i = 0; i < rowCount; i += 1) {
          Vue.set(this.rowState[i], 'shown', true);
        }
      }

      this.querySave && this.querySave();
    },
    applyFormatter: function applyFormatter(format, value) {
      if (value === undefined) { return ''; }

      if (format === 'currency') {
        return `$${parseFloat(value.toFixed(2))}`;
      }
      if (format === 'number_commas') {
        return value.toLocaleString();
      }
      return value;
    },
    tableSearchUpdated: function tableSearchUpdated(reset = false) {
      const filterText = this.searchText.toLowerCase();
      /** Reset internalPageNumber only on actual search updates, or default client tables */
      if (!this.enableQueries || reset) {
        this.internalPageNumber = 0;
        /** Reset InternalSortColumn to original prop or default value */
        this.internalSortColumn = this.originalSortColumnProp;
        this.internalSortDirection = this.originalSortDirectionProp;
      }
      if (this.dataMode === 'client') {
        if (filterText === '') {
          this.applyFilter = 0;
          this.totalRows = this.rowData.length;
        } else { this.applyFilter = 1; }

        const filterFields = [];
        for (let i = 0; i < this.columns.length; i += 1) {
          filterFields.push(this.columns[i].id);
        }

        let filteredRowCount = 0;
        for (let i = 0; i < this.rowState.length; i += 1) {
          let matches = 0;
          const { refId } = this.rowState[i];
          for (let j = 0; j < filterFields.length; j += 1) {
            const key = filterFields[j];
            if ((this.rowData[refId][key] !== undefined)
                && (this.rowData[refId][key] !== null)) {
              if ((this.rowData[refId][key].toString().toLowerCase().indexOf(filterText) !== -1)
                  || (filterText === '')) {
                matches = 1;
              }
            }
          }
          if (matches) {
            Vue.set(this.rowState[i], 'filterMatch', true);
            filteredRowCount += 1;
          } else {
            Vue.set(this.rowState[i], 'filterMatch', false);
            Vue.set(this.rowState[i], 'shown', false);
          }
        }
        this.totalRows = filteredRowCount;
        if (filteredRowCount < this.internalItemsPerPage) {
          this.pageCount = 1;
        } else {
          this.pageCount = Math.floor(filteredRowCount / this.internalItemsPerPage);
          this.querySanitizeCurrentPage && this.querySanitizeCurrentPage();
        }
        this.querySave && this.querySave();
        if (reset) this.rowState.sort(this.rowSort());
        return this.updateRowVisibility();
      }
      return this.tablePropsUpdated();
    },
    toggleColumnSorting: function toggleColumnSorting(col) {
      if (col.hideSort) {
        return;
      }

      this.internalSortColumn = col.id;

      if ((this.internalSortDirection === '')
          || (this.internalSortDirection === 'descending')) {
        this.internalSortDirection = 'ascending';
      } else {
        this.internalSortDirection = 'descending';
      }
      if (this.dataMode === 'client') {
        this.rowState.sort(this.rowSort());
        this.updateRowVisibility();
      } else {
        this.tablePropsUpdated();
      }
    },
    getSortIcon: function getSortIcon(col) {
      if (col.id === this.internalSortColumn) {
        if (this.internalSortDirection === 'ascending') {
          return 'table-sorter-ascending';
        } if (this.internalSortDirection === 'descending') {
          return 'table-sorter-descending';
        }
      }
      return 'table-sorter';
    },
    isShownRowEven: function isShownRowEven(index) {
      const isLocal = this.dataMode === 'client';
      if (!isLocal) return !(index % 2);
      if (this.rowShown(index)) {
        let returnValue = !(shownRowIndex % 2);
        shownRowIndex++;
        return returnValue;
      }
      return false;
    },
    rowShown: function rowShown(index) {
      if (this.rowState[index].shown && !this.applyFilter) { return true; }
      if (this.rowState[index].shown && this.applyFilter && this.rowState[index].filterMatch) {
        return true;
      }
      return false;
    },
    rowSort: function rowSort() {
      if (!this.internalSortColumn || this.dataMode === 'server') { return undefined; }

      let sortOrder = 1;
      const sortCol = this.internalSortColumn;

      if (this.internalSortDirection === 'descending') {
        sortOrder = -1;
      }
      const context = this;

      return function sortfunc(a, b) {
        let result = 0;
        const aRow = context.rowData[a.refId];
        const bRow = context.rowData[b.refId];

        if (typeof aRow[sortCol] === 'string' && typeof bRow[sortCol] === 'string') {
          if (aRow[sortCol].toUpperCase() < bRow[sortCol].toUpperCase()) { result = -1; }
          if (bRow[sortCol].toUpperCase() < aRow[sortCol].toUpperCase()) { result = 1; }
        } else {
          if (aRow[sortCol] < bRow[sortCol]) { result = -1; }
          if (bRow[sortCol] < aRow[sortCol]) { result = 1; }
        }
        return result * sortOrder;
      };
    },
    isOpen(index) {
      return this.rowState[index].expanded;
    },
    isRowSelected(index) {
      return this.rowState[index].selected;
    },
    toggleRow(index, isOpen) {
      Vue.set(this.rowState[index], 'expanded', isOpen);
    },
    expandColumnWidth(id) {
      this.internalColumnData[id].width = '100%';
    },
    columnStyle: function columnStyle(id) {
      let style = '';
      if (this.internalColumnData[id].width !== undefined) {
        style += `width: ${this.internalColumnData[id].width}`;
      }
      return style;
    },
    columnTooltip: function columnTooltip(id) {
      let tooltip = '';
      if (this.internalColumnData[id].title !== undefined) {
        tooltip = this.internalColumnData[id].title;
      }
      return tooltip;
    },
    selectAllRowsClicked: function selectAllRowsClicked(checked, override = false) {
      const value = override ? !this.internalAllSelected : checked;
      if (value) {
        for (let i = 0; i < this.rowState.length; i += 1) {
          if (this.rowState[i].filterMatch && (this.rowState[i].shown || this.selectAllOverride)) {
            Vue.set(this.rowState[i], 'selected', true);
          } else {
            Vue.set(this.rowState[i], 'selected', false);
          }
        }
      } else {
        for (let i = 0; i < this.rowState.length; i += 1) {
          Vue.set(this.rowState[i], 'selected', false);
        }
      }
      if (override) {
        this.internalAllSelected = this.checkIfAllRowsSelected()
        try {
          const { checkbox: internalCheckbox } = (this.$refs.tableCheckbox || { $refs: {} }).$refs;
          const { checkbox: input } = (internalCheckbox || { $refs: {} }).$refs;
          if (input && input.checked !== this.internalAllSelected) {
            input.checked = this.internalAllSelected;
          }
        } catch (err) {
          console.error(err);
        }
      }
      this.$emit('selection-updated', this.selectedRows);
    },
    checkIfAllRowsSelected() {
      return [this.internalItemsPerPage, this.rowState.length].includes(this.selectedRows.length)
    },
    selectItem(event, refId) {
      const index = this.rowState.findIndex(row => row.refId === refId);
      Vue.set(this.rowState[index], 'selected', event.value);
      this.$emit('selection-updated', this.selectedRows, { event, refId, index });
    },
    addButton(event) {
      this.$emit('add-button-clicked', event);
    },
    isOddBackground(index) {
      return index % 2 === 1;
    },
    isEvenBackground(index) {
      return index % 2 === 0;
    },
    // eslint-disable-next-line consistent-return
    addAriaSort: function addAriaSort(key) {
      if (key === this.internalSortColumn) {
        const dir = this.internalSortDirection;
        if (dir === 'ascending' || dir === 'descending') {
          return dir;
        }
        return (!dir) ? 'none' : 'other';
      }
    },
    getOptions(defaultOptions, options) {
      const opts = {
        ...defaultOptions,
      };
      if (options) {
        Object.keys(defaultOptions).forEach((key) => {
          if (options[key] || typeof options[key] === 'boolean') {
            opts[key] = options[key];
          }
        });
      }
      return opts;
    },
    getTabindex(type = '') {
      if (type === 'td') return this.enableCellNavigation ? '0' : '-1';
      if (type === 'checkbox') return this.enableCellNavigation ? '-1' : '0';
      return '';
    },
    isCellVisible(column, index) {
      return this.collapseOptions.visible.some((item) => item === column.label || item === index);
    },
    handleHeaderClick(event, column) {
      if (column.id === 'action') { this.addButton(event); } else {
        this.toggleColumnSorting(column);
      }
    },
    exportCsv(mode) {
      if (this.customCsvExport) {
        if (mode === 'selection') {
          this.$emit('csv-export', this.selectedRows);
        } else {
          this.$emit('csv-export', this.rowData);
        }
        return;
      }

      const csvExporter = new ExportToCsv(this.csvExportOptions);
      if (!this.csvExportHasHiddenColumns) {
        if (mode === 'selection') {
          csvExporter.generateCsv(this.selectedRows);
        } else {
          csvExporter.generateCsv(this.rowData);
        }
      } else {
        let rowsToProcess = [];
        if (mode === 'selection') {
          rowsToProcess = JSON.parse(JSON.stringify(this.selectedRows));
        } else {
          rowsToProcess = JSON.parse(JSON.stringify(this.rowData));
        }
        // eslint-disable-next-line prefer-const
        let filteredData = rowsToProcess.filter((row) => {
          this.columns.forEach((column) => {
            if (column.excludeFromExport) { delete row[column.id]; }
          });
          return row;
        });
        csvExporter.generateCsv(filteredData);
      }
    },
    exportSelectedRows() {
      this.exportCsv('selection');
    },
    borderAlertStyle(row) {
      const needsAlert = this.borderAlerts.properties.find((property) => {
        return row[property.key] === property.value
      })
      if(needsAlert) {
        return 'border-alert'
      } return {};
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/colors.scss';
table {
  border-spacing: 0;
  border-collapse: separate;
  border-top: var(--table-border-top);
  table-layout: fixed;
  width: 100%;
  position: relative;

  td {

    &:focus,
    &.active,
    &:focus-visible,
    &:focus-within {
      outline: none;
    }
  }
  &[data-navigation] {
    td {

      &:focus,
      &:active,
      &.active,
      &:focus-visible,
      &:focus-within {
        /**
        * outline is a native color based on users device/browser when an element is in focus
        * Docs: https://developer.mozilla.org/en-US/docs/Web/CSS/outline
        */
        outline: #016699 2px solid !important;
      }
    }
    a, ::v-deep a {
      &:focus-visible {
        /**
        * outline is a native color based on users device/browser when an element is in focus
        * Docs: https://developer.mozilla.org/en-US/docs/Web/CSS/outline
        */
        outline: dashed !important;
      }
    }
  }
}

th,
td {
  border-bottom: 1px solid #dbdbdb;
  border-left: 1px solid #dbdbdb;
  color: #4F4F4F;
  text-align: left;

  &:nth-child(1) {
    border-left: none !important;
  }
}

th {
  padding: 0;
  margin: 0;
  user-select: none;
}

th.sortable {
  cursor: pointer;
}

.vcl-table {
  $self: &;

  table {
    margin: 0;
    padding: 0;
  }

  &__body {
    & > tr:nth-child(odd) {
      background-color: $white;
    }

    & > tr:nth-child(even) {
      background-color: #F4F7F8;
    }
  }

  &__header,
  &__body {
    #{ $self }__selection {
      padding: 0;
      text-align: center;
      width: 55px;
      min-width: 55px;

      .toggle-row-icon {
        font-size: 8px;
        color: #4F4F4F;
        padding: 6px;
        border: 1px solid #DBDBDB;
        border-radius: 2px;
        background-color: #fff;
        box-sizing: content-box;

        &.is-icon-open {
          transform: rotate(180deg);
        }
      }
    }

    #{ $self }__action {
      padding: 0;
      text-align: center;
      width: 79px;
      min-width: 79px;
      overflow: visible;
      font-size: 16px;
      font-style: normal;
      font-weight: 400;
      line-height: 1.15;
    }

    #{ $self }__row {

      &.is-row-open {
        &.small-breakpoint {
          @media screen and (max-width: 40em) {
            border-left: solid #016699 4px;
          }
        }

        &.medium-breakpoint {
          @media screen and (max-width: 64em) {
            border-left: solid #016699 4px;
          }
        }

        &.large-breakpoint {
          @media screen and (max-width: 75em) {
            border-left: solid #016699 4px;
          }
        }
      }

      .column-header {
        gap: .5rem;
        display: flex;
        min-height: 56px;
        padding-top: .5rem;
        padding-bottom: .5rem;
        align-items: center;
        box-sizing: border-box;

        &__label {
          margin: 0;
          padding: 0;
          font-size: 16px;
          font-style: normal;
          font-weight: 600;
          line-height: 1.15;
          overflow: hidden;
        }

        &__sort-icon {
          margin: 0;
          padding: 0;

          .table-sorter {
            font-size: 10px;
            color: #4F4F4F;
            margin: 0 0 2px 0;
            padding: 0;

            &-ascending,
            &-descending {
              font-size: 7px;
              padding: 0;
              margin: 0;
              margin-left: 1px;
              color: #4F4F4F;
            }
          }
        }
      }
    }
  }

  &__header {
    &[data-sticky] {
      position: sticky;
      background: #fff;
      transition: top 500ms;
      top: var(--table-thead-top);
      z-index: var(--table-thead-index);
    }

    #{ $self }__action {
      font-weight: 600;
    }
  }

  &__empty-state {
    background-color: #FBFBFB;
    min-height: 113px;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    color: #4F4F4F;
    border-bottom: 1px solid #dbdbdb;

    #{ $self }__empty-state-content {
      display: flex;
      flex-direction: column;
      align-items: center;
      max-width: 340px;
      margin: 0;
      padding: 74px 0;

      #{ $self }__empty-state-title {
        color: #1B3664;
        margin: 0 0 19px;
        text-align: center;
      }

      #{ $self }__empty-state-description {
        color: #4F4F4F;
        margin: 0;
        text-align: center;

        &::v-deep a {
          color: #016699;
          text-decoration: underline;
          font-size: 16px;
        }
      }
    }
  }
}

tr {
  height: 56px;

  &.expanded-row {
    box-shadow: inset 4px 0px #016699;
  }
}

tr td {
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  color: #4F4F4F;

  a {
    color: #016699;
    font-weight: 400;
    text-decoration-line: underline;
  }
}

tr td.vcl-table__selection {
  text-overflow: unset;
}

tr.clickable td {
  cursor: pointer;
}

tr.rowSelected {
  background-color: #E9F6FE !important;
}

th.leftAlign,
td.leftAlign {
  padding-left: 20px;
  text-align: left;
}

th.centerAlign,
td.centerAlign {
  text-align: center;
}

th.rightAlign,
td.rightAlign {
  padding-right: 20px;
  text-align: right;
}

.column-header__label {
  gap: 0.20rem;
  display: flex;
  flex-direction: column;
  &__sub {
    color: #4F4F4F;
    font-size: 0.6875rem;
  }
}

div.align-left {
  justify-content: flex-start;
  padding-left: 20px;
  padding-right: 10px;

  .column-header__label {
    text-align: left;
  }
}

div.align-center {
  justify-content: center;

  .column-header__label {
    text-align: center;
  }
}

div.align-right {
  justify-content: flex-end;
  padding-right: 15px;
  padding-left: 5px;

  .column-header__label {
    text-align: right;
  }
}


#sort-message {
  display: none;
}

.vcl-table__pagination-controls {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 17px 24px;
  min-height: 22px;
  .vcl-table__footer {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items: center;
    .vcl-table__results {
      margin-right: 16px;
      span {
        font-size: 16px;
        color: #4F4F4F;
      }
    }

    .vcl-table__selection {
      display: flex;

      p {
        font-size: 16px;
        margin: 0;
        padding: 0;
        margin-right: 6px;
      }

      select {
        height: 30px;
        margin: -3px 6px 0 0;
      }
    }
    &:only-child {
      margin-left: auto;
    }
  }
}

select.itemsPerPageSelection {
  padding: 5px 18px 5px 8px;
  width: 70px;
  display: flex;
}

div.vcl-table__pagination {
  display: inline-block;
}

/***** START: COLLAPSE ON MOBILE *****/
.hide-on-breakpoint {
  &.small-breakpoint {
    @media screen and (max-width: 40em) {
      display: none !important;
    }
  }

  &.medium-breakpoint {
    @media screen and (max-width: 64em) {
      display: none !important;
    }
  }

  &.large-breakpoint {
    @media screen and (max-width: 75em) {
      display: none !important;
    }
  }
}

.show-on-breakpoint {
  &.small-breakpoint {
    @media screen and (min-width: 40em) {
      display: none !important;
    }
  }

  &.medium-breakpoint {
    @media screen and (min-width: 64em) {
      display: none !important;
    }
  }

  &.large-breakpoint {
    @media screen and (min-width: 75em) {
      display: none !important;
    }
  }
}

.expand-on-breakpoint {
  &.small-breakpoint {
    @media screen and (max-width: 40em) {
      width: 100% !important;
    }
  }

  &.medium-breakpoint {
    @media screen and (max-width: 64em) {
      width: 100% !important;
    }
  }

  &.large-breakpoint {
    @media screen and (max-width: 75em) {
      width: 100% !important;
    }
  }
}

.show-row-on-breakpoint {
  &.small-breakpoint {
    @media screen and (max-width: 40em) {
      display: table-cell !important;
    }
  }

  &.medium-breakpoint {
    @media screen and (max-width: 64em) {
      display: table-cell !important;
    }
  }

  &.large-breakpoint {
    @media screen and (max-width: 75em) {
      display: table-cell !important;
    }
  }
}

.hide-collapse-trigger {
  display: none;
}

.collapsed-row {
  display: table-row;
  border-left: solid 4px #016699;
  background: #FBFBFB;
  box-shadow: inset 0px 1px 3px rgba(0, 0, 0, 0.05);

  &__content {
    margin: 0;
    border-bottom: 1px solid #ECECEC;
    padding: 8px 24px;
    color: #4F4F4F;
    font-size: 13px;
  }

  &__content:nth-last-child(1) {
    border-bottom: none;
  }
}

/***** END: COLLAPSE ON MOBILE *****/
</style>
